import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { refreshAssetMeta, COMPANY_COLORS } from '../lib/assets';
import InstructionTemplates from './InstructionTemplates';
import CompanyChip from './asset-company/CompanyChip';
import './Assets.css';

/**
 * Settings → Assets & devices. Administrators only, like everything in Settings.
 *
 * The parts, in the order somebody setting the register up needs them:
 *
 *   Switches      whether the register is in use, and whether a ticket may
 *                 name somebody else's device
 *   Companies     whose devices share the register (062): name, code, colour
 *   Places        the location tree
 *   Categories    Laptop, Monitor... with their tag prefixes
 *   Permissions   who may read or change which part of the register - a
 *                 person or, since 062, a contractor; the whole register or
 *                 a part of it
 *
 * Every answer from the server is shown as it came - an error says what the
 * server said rather than "something went wrong", because the refusals here
 * ("this place still has 3 devices") are the instructions.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const errorOf = (e, fallback) => e.response?.data?.error || fallback;

/*
 * EACH PART IS A PAGE OF ITS OWN NOW (26 October). This used to draw its own
 * strip of seven sub-tabs that had no address; Settings -> Assets & devices
 * lists nine pages, each with one, and draws this with `page` naming which.
 * Switches split in two (the register's switches; reminders and the label
 * size) and so did Devices (what the agent may do; the enrolment queue,
 * tokens and office networks). Nothing in any part changed.
 */
const PAGES = {
  register: () => <Switches half="register" />,
  companies: () => <Companies />,
  places: () => <Places />,
  'device-types': () => <Categories />,
  'register-access': () => <Permissions />,
  'reminders-labels': () => <Switches half="reminders" />,
  'device-agent': () => <Devices half="agent" />,
  enrolment: () => <Devices half="enrolment" />,
  instructions: () => <InstructionTemplates />
};

export default function AssetSettings({ page = 'register' }) {
  const [missing, setMissing] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/asset-admin/categories`)
      .then(() => setMissing(null))
      .catch((e) => {
        if (e.response?.status === 503) setMissing(errorOf(e, 'The register is not set up yet.'));
      });
  }, []);

  if (missing) {
    return (
      <div className="as-settings">
        <p className="as-note" data-tone="warning">{missing}</p>
      </div>
    );
  }

  const Part = PAGES[page] || PAGES.register;
  return (
    <div className="as-settings">
      <Part />
    </div>
  );
}

// ------------------------------------------------------------- switches

function Switches({ half = 'register' }) {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => axios.get(`${API_BASE}/app-settings`)
    .then((r) => setRules(r.data || {}))
    .catch(() => setError('Could not load the settings.')), []);
  useEffect(() => { load(); }, [load]);

  const set = async (key, path, value) => {
    setSaving(key);
    setError('');
    try {
      await axios.put(`${API_BASE}/app-settings/${path}`, { value });
      await load();
      refreshAssetMeta();
    } catch (e) {
      setError(errorOf(e, 'Could not save that setting.'));
    } finally {
      setSaving(null);
    }
  };

  if (!rules) return <p className="as-quiet">Loading…</p>;
  const on = rules.assets_enabled === true;
  const anyDevice = rules.assets_ticket_any_device !== false;
  const myEquipment = rules.assets_my_equipment === true;
  if (half === 'reminders') {
    return (
      <div className="as-section">
        {error && <p className="as-error" role="alert">{error}</p>}
        {'assets_warranty_notice_days' in rules && (
          <NoticeDays value={rules.assets_warranty_notice_days} onSaved={load} />
        )}
        {'assets_label_custom' in rules && (
          <LabelSize value={rules.assets_label_custom} onSaved={load} />
        )}
      </div>
    );
  }
  return (
    <div className="as-section">
      {error && <p className="as-error" role="alert">{error}</p>}
      <div className="ar-rule" id="s-assets-enabled">
        <label className="ar-check">
          <input type="checkbox" checked={on} disabled={saving === 'assets_enabled'}
            onChange={() => set('assets_enabled', 'assets-enabled', !on)} />
          <span className="ar-label">The asset register is in use</span>
        </label>
        <p className="ar-consequence">
          {on
            ? 'On: staff see a Device choice on Create Ticket, and anybody with a grant sees Assets in the menu.'
            : 'Off: only administrators see the register, so IT can enter devices before anybody is asked to choose one.'}
        </p>
      </div>
      <div className="ar-rule" id="s-any-device">
        <label className="ar-check">
          <input type="checkbox" checked={anyDevice} disabled={saving === 'assets_ticket_any_device'}
            onChange={() => set('assets_ticket_any_device', 'assets-ticket-any-device', !anyDevice)} />
          <span className="ar-label">A ticket may name somebody else’s device</span>
        </label>
        <p className="ar-consequence">
          {anyDevice
            ? 'On: anybody raising a ticket can search the whole register by tag, serial or holder — for the laptop that changed hands before the register did.'
            : 'Off: people can choose only their own devices and shared equipment.'}
        </p>
      </div>
      {'assets_my_equipment' in rules && (
        <div className="ar-rule" id="s-my-equipment">
          <label className="ar-check">
            <input type="checkbox" checked={myEquipment} disabled={saving === 'assets_my_equipment'}
              onChange={() => set('assets_my_equipment', 'assets-my-equipment', !myEquipment)} />
            <span className="ar-label">Staff see My equipment</span>
          </label>
          <p className="ar-consequence">
            {myEquipment
              ? 'On: everybody has a My equipment page listing the devices assigned to them, with Report a problem and This is not mine.'
              : 'Off: nobody is shown a list of their devices. Switch it on once the register is right about who has what.'}
          </p>
        </div>
      )}
      <PurgeRegister />
    </div>
  );
}

/**
 * START THE REGISTER AGAIN (105): the SQL script Gordon was given, as a
 * setting. Shows what would go, then needs PURGE typed. The server does the
 * work and says where the numbering restarts.
 */
function PurgeRegister() {
  const [info, setInfo] = useState(null);
  const [keepReporting, setKeepReporting] = useState(true);
  const [forgetPersonal, setForgetPersonal] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(() => axios.get(`${API_BASE}/asset-admin/purge`)
    .then((r) => setInfo(r.data))
    .catch((e) => setError(errorOf(e, 'Could not read what the register holds.'))), []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/asset-admin/purge`,
        { keep_reporting: keepReporting, forget_personal: forgetPersonal, confirm: 'PURGE' });
      setResult(r.data);
      setTyped('');
      load();
      refreshAssetMeta();
    } catch (e) {
      setError(errorOf(e, 'Could not start the register again.'));
    } finally { setBusy(false); }
  };

  const n = (v) => (typeof v === 'number' ? v : 0);
  return (
    <div className="ar-rule" id="s-purge" data-testid="purge-card"
      style={{ border: '1px solid var(--color-critical)', background: 'var(--color-critical-light)', borderRadius: 'var(--radius-sm)', padding: '0.9rem 1rem', marginTop: '1.5rem' }}>
      <span className="ar-label" style={{ color: 'var(--color-critical)' }}>Start the register again</span>
      <p className="ar-consequence">
        Deletes the devices in the register, with their history, and restarts the tag numbering. This cannot be undone.
        Numbering restarts at 1 for each type, after the numbers of any computers you keep.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      {info && (
        <p data-testid="purge-preview">
          {`${n(info.devices)} device${n(info.devices) === 1 ? '' : 's'} would be deleted, along with ${n(info.inbox_rows)} Discovery inbox row${n(info.inbox_rows) === 1 ? '' : 's'}. `}
          {`${n(info.personal_items)} personal item${n(info.personal_items) === 1 ? ' is' : 's are'} recorded.`}
        </p>
      )}
      {result && (
        <div className="as-note" data-testid="purge-result" role="status">
          <p>{`Deleted ${n(result.deleted)} device${n(result.deleted) === 1 ? '' : 's'}, kept ${n(result.kept)}, cleared ${n(result.inbox_cleared)} inbox row${n(result.inbox_cleared) === 1 ? '' : 's'}${n(result.marks_cleared) ? ` and ${n(result.marks_cleared)} personal mark${n(result.marks_cleared) === 1 ? '' : 's'}` : ''}.`}</p>
          {(result.numbering || []).length > 0 && (
            <ul className="as-tree">
              {result.numbering.map((x) => (
                <li key={x.prefix || x.category}><strong>{x.category}</strong><span>{`next tag ${x.prefix}-${String(x.next_number).padStart(6, '0')}`}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
      <label className="as-check">
        <input type="checkbox" checked={keepReporting} onChange={(e) => setKeepReporting(e.target.checked)} />
        {`Keep the computers whose agents report in (${n(info?.reporting)})`}
      </label>
      <label className="as-check">
        <input type="checkbox" checked={forgetPersonal} onChange={(e) => setForgetPersonal(e.target.checked)} />
        {`Also forget what people marked personal (${n(info?.personal_marks)})`}
      </label>
      <div className="as-inline-form">
        <div className="form-group" style={{ flex: '0 1 14rem' }}>
          <label htmlFor="as-purge-confirm">Type PURGE to confirm</label>
          <input id="as-purge-confirm" value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
        </div>
        <button type="button" className="btn btn-danger" disabled={busy || typed !== 'PURGE' || !info} onClick={run}>
          {busy ? 'Working…' : 'Delete and start again'}
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- companies

/** Nine swatches and "none", pressed like a radio group. */
function Swatches({ value, onChange, idPrefix }) {
  return (
    <div className="as-swatches" role="group" aria-label="Colour">
      {COMPANY_COLORS.map((c) => (
        <button key={c} type="button" className="as-swatch" data-color={c} aria-label={c}
          aria-pressed={value === c} id={`${idPrefix}-${c}`} onClick={() => onChange(value === c ? null : c)} />
      ))}
      <button type="button" className="as-swatch" aria-label="No colour" aria-pressed={!value} onClick={() => onChange(null)} />
    </div>
  );
}

/**
 * The companies whose devices share the register (062). Made here, given a
 * short code for crowded rows and a colour for the chip, put in the order
 * the filter lists them, and archived - never deleted - once devices point
 * at one.
 */
function Companies() {
  const [rows, setRows] = useState(null);
  const [missing, setMissing] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', short_code: '', color: null });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => axios.get(`${API_BASE}/asset-admin/companies`)
    .then((r) => { setRows(r.data || []); setMissing(''); })
    .catch((e) => {
      if (e.response?.status === 503) { setMissing(errorOf(e, 'Companies need migration 062.')); setRows([]); } else setError(errorOf(e, 'Could not load the companies.'));
    }), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { await fn(); await load(); refreshAssetMeta(); return true; } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (await act(() => axios.post(`${API_BASE}/asset-admin/companies`, form))) setForm({ name: '', short_code: '', color: null });
  };
  const save = async () => {
    const ok = await act(() => axios.put(`${API_BASE}/asset-admin/companies/${editing.id}`,
      { name: editing.name, short_code: editing.short_code || '', color: editing.color || null }));
    if (ok) setEditing(null);
  };
  const move = (i, by) => {
    const live = rows.filter((c) => !c.archived_at);
    const j = i + by;
    if (j < 0 || j >= live.length) return;
    const order = [...live];
    [order[i], order[j]] = [order[j], order[i]];
    act(() => axios.post(`${API_BASE}/asset-admin/companies/reorder`, { ids: [...order, ...rows.filter((c) => c.archived_at)].map((c) => c.id) }));
  };

  if (missing) return <div className="as-section"><p className="as-note" data-tone="warning">{missing}</p></div>;
  if (!rows) return <p className="as-quiet">Loading…</p>;
  const live = rows.filter((c) => !c.archived_at);
  const archived = rows.filter((c) => c.archived_at);

  const row = (c, i, isLive) => (editing?.id === c.id ? (
    <li key={c.id}>
      <input value={editing.name} aria-label="Name" onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
      <input value={editing.short_code || ''} aria-label="Short code" maxLength={8} style={{ flex: '0 1 6rem' }}
        onChange={(e) => setEditing({ ...editing, short_code: e.target.value.toUpperCase() })} />
      <Swatches value={editing.color} onChange={(color) => setEditing({ ...editing, color })} idPrefix={`as-co-edit-${c.id}`} />
      <button type="button" className="btn btn-primary btn-small" onClick={save}>Save</button>
      <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing(null)}>Cancel</button>
    </li>
  ) : (
    <li key={c.id} data-archived={String(!isLive)}>
      {isLive && (
        <span className="as-order">
          <button type="button" aria-label={`Move ${c.name} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
          <button type="button" aria-label={`Move ${c.name} down`} disabled={i === live.length - 1} onClick={() => move(i, 1)}>↓</button>
        </span>
      )}
      <CompanyChip name={c.name} color={c.color} />
      {c.short_code && <span className="as-tag">{c.short_code}</span>}
      <span className="as-quiet">{`${c.asset_count} device${c.asset_count === 1 ? '' : 's'}`}{c.grant_count ? ` · ${c.grant_count} grant${c.grant_count === 1 ? '' : 's'}` : ''}</span>
      <span className="as-spacer" />
      {isLive && <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing({ ...c })}>Edit</button>}
      <button type="button" className="btn btn-plain btn-small"
        onClick={() => act(() => axios.put(`${API_BASE}/asset-admin/companies/${c.id}`, { archived: isLive }))}>
        {isLive ? 'Archive' : 'Bring back'}
      </button>
      {c.asset_count === 0 && c.grant_count === 0 && (
        <button type="button" className="btn btn-plain btn-small"
          onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/companies/${c.id}`))}>Delete</button>
      )}
    </li>
  ));

  return (
    <div className="as-section" data-testid="companies">
      <p className="as-quiet">
        One register, several owners: each device can belong to a company, the list filters by it,
        and a grant can be narrowed to one — “Marco looks after Alpha’s laptops”. A company with
        devices is archived rather than deleted, so those devices keep its name.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <form className="as-inline-form" onSubmit={add}>
        <div className="form-group">
          <label htmlFor="as-co-name">New company</label>
          <input id="as-co-name" value={form.name} required maxLength={80}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alpha Ltd" />
        </div>
        <div className="form-group" style={{ flex: '0 1 7rem' }}>
          <label htmlFor="as-co-code">Short code</label>
          <input id="as-co-code" value={form.short_code} maxLength={8}
            onChange={(e) => setForm({ ...form, short_code: e.target.value.toUpperCase() })} placeholder="ALPH" />
        </div>
        <div className="form-group" style={{ flex: '0 1 auto' }}>
          <span className="ar-label" style={{ display: 'block', marginBottom: '0.35rem' }}>Colour</span>
          <Swatches value={form.color} onChange={(color) => setForm({ ...form, color })} idPrefix="as-co-new" />
        </div>
        <button type="submit" className="btn btn-primary">Add</button>
      </form>
      {live.length === 0 && archived.length === 0
        ? <p className="as-quiet">No companies yet. Without any, nothing on the register mentions one.</p>
        : <ul className="as-tree">{live.map((c, i) => row(c, i, true))}</ul>}
      {archived.length > 0 && (
        <>
          <h3>Archived</h3>
          <ul className="as-tree">{archived.map((c, i) => row(c, i, false))}</ul>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- places

/** A floor number typed in a box: blank is "not set" (null). */
const floorOf = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// Storage (104): what a place is. Storage areas, cupboards and shelves hold
// devices that are too many to pin; the plan shows one icon per storage area.
const PLACE_TYPES = {
  area: 'Area', storage: 'Storage area', container: 'Cupboard or shelving unit', shelf: 'Shelf'
};
const capacityOf = (f) => ((f.kind || 'area') === 'area' || f.capacity === '' || f.capacity == null
  ? null : Number(f.capacity));

function Places() {
  const [places, setPlaces] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', level_label: '', parent_id: '', floor_number: '', kind: 'area', capacity: '' });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => axios.get(`${API_BASE}/asset-admin/locations`)
    .then((r) => setPlaces(r.data || []))
    .catch((e) => setError(errorOf(e, 'Could not load the places.'))), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { await fn(); await load(); refreshAssetMeta(); return true; } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const ok = await act(() => axios.post(`${API_BASE}/asset-admin/locations`, {
      name: form.name, level_label: form.level_label, parent_id: form.parent_id || null, floor_number: floorOf(form.floor_number),
      kind: form.kind, capacity: capacityOf(form)
    }));
    if (ok) setForm({ name: '', level_label: '', parent_id: form.parent_id, floor_number: '', kind: 'area', capacity: '' });
  };

  const save = async () => {
    const ok = await act(() => axios.put(`${API_BASE}/asset-admin/locations/${editing.id}`, {
      name: editing.name, level_label: editing.level_label, parent_id: editing.parent_id || null, floor_number: floorOf(editing.floor_number),
      kind: editing.kind || 'area', capacity: capacityOf(editing)
    }));
    if (ok) setEditing(null);
  };

  return (
    <div className="as-section">
      <p className="as-quiet">
        Build the tree as you need it: a site today, a floor or a room when it helps. A grant on a
        place covers everything beneath it. Remote workers’ homes appear under Remote by themselves.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <form className="as-inline-form" onSubmit={add}>
        <div className="form-group">
          <label htmlFor="as-place-name">New place</label>
          <input id="as-place-name" value={form.name} required
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Room 204" />
        </div>
        <div className="form-group">
          <label htmlFor="as-place-level">Kind</label>
          <input id="as-place-level" value={form.level_label} list="as-levels"
            onChange={(e) => setForm({ ...form, level_label: e.target.value })} placeholder="Room" />
        </div>
        <div className="form-group">
          <label htmlFor="as-place-parent">Inside</label>
          <select id="as-place-parent" value={form.parent_id}
            onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
            <option value="">Nothing — a new site</option>
            {places.map((p) => <option key={p.id} value={p.id}>{p.path}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-place-type">Type</label>
          <select id="as-place-type" value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {Object.entries(PLACE_TYPES).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
          </select>
        </div>
        {form.kind !== 'area' && (
          <div className="form-group">
            <label htmlFor="as-place-capacity">Capacity</label>
            <input id="as-place-capacity" type="number" min="1" max="10000" step="1" value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </div>
        )}
        <div className="form-group">
          <label htmlFor="as-place-floor">Floor number</label>
          <input id="as-place-floor" type="number" min="-10" max="200" step="1" value={form.floor_number}
            onChange={(e) => setForm({ ...form, floor_number: e.target.value })} />
        </div>
        <button type="submit" className="btn btn-primary">Add</button>
      </form>
      <p className="as-quiet">For stacking floors in the building view: basements negative, ground 0.</p>
      <datalist id="as-levels">
        {['Site', 'Building', 'Floor', 'Room', 'Shelf', 'Cabinet'].map((l) => <option key={l} value={l} />)}
      </datalist>

      {places.length === 0
        ? <p className="as-quiet">No places yet.</p>
        : (
          <ul className="as-tree">
            {places.map((p) => (
              <li key={p.id} style={{ paddingLeft: `${(p.depth || 0) * 1.25}rem` }}>
                {editing?.id === p.id ? (
                  <>
                    <input value={editing.name} aria-label="Name"
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                    <input value={editing.level_label || ''} aria-label="Kind" list="as-levels"
                      onChange={(e) => setEditing({ ...editing, level_label: e.target.value })} />
                    <select value={editing.parent_id || ''} aria-label="Inside"
                      onChange={(e) => setEditing({ ...editing, parent_id: e.target.value })}>
                      <option value="">Nothing — a site</option>
                      {places.filter((x) => x.id !== p.id).map((x) => (
                        <option key={x.id} value={x.id}>{x.path}</option>
                      ))}
                    </select>
                    <input type="number" min="-10" max="200" step="1" aria-label="Floor number"
                      value={editing.floor_number ?? ''}
                      onChange={(e) => setEditing({ ...editing, floor_number: e.target.value })} />
                    <select value={editing.kind || 'area'} aria-label="Type"
                      onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                      {Object.entries(PLACE_TYPES).map(([k, w]) => <option key={k} value={k}>{w}</option>)}
                    </select>
                    {(editing.kind || 'area') !== 'area' && (
                      <input type="number" min="1" max="10000" step="1" aria-label="Capacity"
                        value={editing.capacity ?? ''}
                        onChange={(e) => setEditing({ ...editing, capacity: e.target.value })} />
                    )}
                    <button type="button" className="btn btn-primary btn-small" onClick={save}>Save</button>
                    <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <strong>{p.name}</strong>
                    {p.level_label && <span className="as-chip">{p.level_label}</span>}
                    {p.kind && p.kind !== 'area' && (
                      <span className="as-chip" data-tone="info">
                        {PLACE_TYPES[p.kind] || p.kind}{p.capacity ? ` · capacity ${p.capacity}` : ''}
                      </span>
                    )}
                    {p.floor_number !== null && p.floor_number !== undefined && <span className="as-quiet">{`Floor ${p.floor_number}`}</span>}
                    {p.home_user_name && <span className="as-quiet">{p.home_user_name}’s home</span>}
                    <span className="as-quiet">
                      {p.asset_count} device{p.asset_count === 1 ? '' : 's'}
                    </span>
                    <span className="as-spacer" />
                    <button type="button" className="btn btn-plain btn-small"
                      onClick={() => setEditing({ ...p })}>Edit</button>
                    <button type="button" className="btn btn-plain btn-small"
                      onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/locations/${p.id}`))}>
                      Delete
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

// ------------------------------------------------------------ categories

function Categories() {
  const [cats, setCats] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', tag_prefix: '' });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => axios.get(`${API_BASE}/asset-admin/categories`)
    .then((r) => setCats(r.data || []))
    .catch((e) => setError(errorOf(e, 'Could not load the categories.'))), []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { await fn(); await load(); refreshAssetMeta(); return true; } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (await act(() => axios.post(`${API_BASE}/asset-admin/categories`, form))) {
      setForm({ name: '', tag_prefix: '' });
    }
  };

  const fieldsText = (schema) => (schema || []).map((f) => f.label).join(', ');
  const save = async () => {
    const labels = String(editing.fields || '').split(',').map((x) => x.trim()).filter(Boolean);
    const ok = await act(() => axios.put(`${API_BASE}/asset-admin/categories/${editing.id}`, {
      name: editing.name,
      tag_prefix: editing.tag_prefix,
      spec_schema: labels.map((label) => {
        const known = (editing.spec_schema || []).find((f) => f.label === label);
        return known || { label };
      })
    }));
    if (ok) setEditing(null);
  };

  return (
    <div className="as-section">
      <p className="as-quiet">
        Each category numbers its own tags: IT-LAP-000001, IT-LAP-000002. A new prefix applies to
        devices added from now on; tags already printed on labels never change.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <form className="as-inline-form" onSubmit={add}>
        <div className="form-group">
          <label htmlFor="as-cat-name">New category</label>
          <input id="as-cat-name" value={form.name} required
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tablet" />
        </div>
        <div className="form-group">
          <label htmlFor="as-cat-prefix">Tag prefix</label>
          <input id="as-cat-prefix" value={form.tag_prefix} required
            onChange={(e) => setForm({ ...form, tag_prefix: e.target.value.toUpperCase() })}
            placeholder="IT-TAB" />
        </div>
        <button type="submit" className="btn btn-primary">Add</button>
      </form>
      <div className="as-table-wrap">
        <table className="as-table">
          <thead>
            <tr><th>Category</th><th>Prefix</th><th>Spec sheet</th><th>Devices</th><th /></tr>
          </thead>
          <tbody>
            {cats.map((c) => (editing?.id === c.id ? (
              <tr key={c.id}>
                <td><input value={editing.name} aria-label="Name"
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></td>
                <td><input value={editing.tag_prefix} aria-label="Prefix"
                  onChange={(e) => setEditing({ ...editing, tag_prefix: e.target.value.toUpperCase() })} /></td>
                <td><input value={editing.fields} aria-label="Spec sheet fields, separated by commas"
                  placeholder="Processor, Memory, Screen size"
                  onChange={(e) => setEditing({ ...editing, fields: e.target.value })} /></td>
                <td>{c.asset_count}</td>
                <td>
                  <button type="button" className="btn btn-primary btn-small" onClick={save}>Save</button>{' '}
                  <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing(null)}>Cancel</button>
                </td>
              </tr>
            ) : (
              <tr key={c.id} style={{ cursor: 'default' }}>
                <td data-label="Category">{c.name}{!c.active && <span className="as-quiet"> (off)</span>}</td>
                <td data-label="Prefix"><span className="as-tag">{c.tag_prefix}</span></td>
                <td data-label="Spec sheet">{fieldsText(c.spec_schema) || <span className="as-quiet">None</span>}</td>
                <td data-label="Devices">{c.asset_count}</td>
                <td>
                  <button type="button" className="btn btn-plain btn-small"
                    onClick={() => setEditing({ ...c, fields: fieldsText(c.spec_schema) })}>Edit</button>
                  <button type="button" className="btn btn-plain btn-small"
                    onClick={() => act(() => axios.put(`${API_BASE}/asset-admin/categories/${c.id}`, { active: !c.active }))}>
                    {c.active ? 'Switch off' : 'Switch on'}
                  </button>
                  {c.asset_count === 0 && (
                    <button type="button" className="btn btn-plain btn-small"
                      onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/categories/${c.id}`))}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ----------------------------------------------------------- permissions

/** "Write · Alpha's monitors in Valletta", the scope of a grant in words. */
export function grantScope(g) {
  if (g.asset_id) return `the device ${g.asset_tag}`;
  const whose = g.company_name ? `${g.company_name}’s ` : '';
  const what = g.category_name ? `${whose}${g.category_name.toLowerCase()}s` : (whose ? `${whose}devices` : 'everything');
  if (g.location_path) return `${what} in ${g.location_path}`;
  if (g.category_name || g.company_name) return `every one of ${what}`.replace('every one of everything', 'everything');
  return 'the whole register';
}

function Permissions() {
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [grants, setGrants] = useState([]);
  const [cats, setCats] = useState([]);
  const [places, setPlaces] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ who: 'user', user_id: '', level: 'read', scope: 'whole',
    company_id: '', category_id: '', location_id: '', asset_tag: '' });

  const load = useCallback(async () => {
    try {
      const [u, g, c, l] = await Promise.all([
        axios.get(`${API_BASE}/users`),
        axios.get(`${API_BASE}/asset-admin/grants`),
        axios.get(`${API_BASE}/asset-admin/categories`),
        axios.get(`${API_BASE}/asset-admin/locations`)
      ]);
      setUsers((u.data || []).filter((x) => x.role !== 'system'));
      setGrants(g.data || []);
      setCats(c.data || []);
      setPlaces(l.data || []);
      // Both answer 503 before 062, which is simply "no such choice".
      const [k, co] = await Promise.all([
        axios.get(`${API_BASE}/asset-admin/contractors`).catch(() => null),
        axios.get(`${API_BASE}/asset-admin/companies`).catch(() => null)
      ]);
      setContractors(k ? k.data : null);
      setCompanies(co ? co.data.filter((x) => !x.archived_at) : null);
    } catch (e) {
      setError(errorOf(e, 'Could not load the permissions.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byPerson = useMemo(() => {
    const m = new Map();
    for (const g of grants) {
      const key = g.contractor_id ? `c:${g.contractor_id}` : `u:${g.user_id}`;
      if (!m.has(key)) {
        m.set(key, { name: g.contractor_name || g.user_name || g.user_email, contractor: Boolean(g.contractor_id), grants: [] });
      }
      m.get(key).grants.push(g);
    }
    return [...m.entries()];
  }, [grants]);

  const add = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const narrow = form.scope === 'narrow';
      let assetId = null;
      if (narrow && form.asset_tag.trim()) {
        const found = await axios.get(`${API_BASE}/assets/by-tag/${encodeURIComponent(form.asset_tag.trim())}`).catch(() => null);
        if (!found?.data?.id) throw new Error(`No device has the tag ${form.asset_tag.trim()}.`);
        assetId = found.data.id;
      }
      await axios.post(`${API_BASE}/asset-admin/grants`, {
        [form.who === 'contractor' ? 'contractor_id' : 'user_id']: form.user_id, level: form.level,
        ...(assetId ? { asset_id: assetId } : {
          company_id: narrow && form.company_id ? form.company_id : null,
          category_id: narrow && form.category_id ? form.category_id : null,
          location_id: narrow && form.location_id ? form.location_id : null
        })
      });
      setForm({ ...form, company_id: '', category_id: '', location_id: '', asset_tag: '' });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save the grant.');
    }
  };

  const remove = async (id) => {
    setError('');
    try { await axios.delete(`${API_BASE}/asset-admin/grants/${id}`); await load(); } catch (err) {
      setError(errorOf(err, 'Could not remove the grant.'));
    }
  };

  const narrow = form.scope === 'narrow';
  const preview = narrow ? {
    company_name: companies?.find((c) => c.id === form.company_id)?.name,
    category_name: cats.find((c) => c.id === form.category_id)?.name,
    location_path: places.find((p) => p.id === form.location_id)?.path,
    asset_id: form.asset_tag.trim() || null, asset_tag: form.asset_tag.trim()
  } : {};
  const chosenName = form.who === 'contractor'
    ? contractors?.find((c) => c.id === form.user_id)?.name
    : users.find((u) => u.id === form.user_id)?.display_name;

  return (
    <div className="as-section" data-testid="permissions">
      <p className="as-quiet">
        Nobody but an administrator sees the register until they are given a grant — a member of
        staff, or a contractor, who then finds Assets in their portal. A grant covers the whole
        register, or is narrowed{companies ? ' to a company,' : ''} to a category, to a place and everything
        beneath it, or to one device — “the monitors in Valletta”, “Alpha’s laptops”. The strongest
        grant a person holds wins.
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <form onSubmit={add}>
        {contractors && (
          <div className="as-grant-who" role="radiogroup" aria-label="Who">
            <label className="as-check">
              <input type="radio" name="as-g-who" checked={form.who === 'user'}
                onChange={() => setForm({ ...form, who: 'user', user_id: '' })} />
              A member of staff
            </label>
            <label className="as-check">
              <input type="radio" name="as-g-who" checked={form.who === 'contractor'}
                onChange={() => setForm({ ...form, who: 'contractor', user_id: '' })} />
              A contractor
            </label>
          </div>
        )}
        <div className="as-inline-form" style={{ marginTop: 0 }}>
          <div className="form-group">
            <label htmlFor="as-g-user">{form.who === 'contractor' ? 'Contractor' : 'Person'}</label>
            <select id="as-g-user" value={form.user_id} required
              onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
              <option value="">Choose…</option>
              {form.who === 'contractor'
                ? (contractors || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                : users.filter((u) => u.role !== 'admin').map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
                ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="as-g-level">May</label>
            <select id="as-g-level" value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value })}>
              <option value="read">Look</option>
              <option value="write">Look and change</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="as-g-scope">Over</label>
            <select id="as-g-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
              <option value="whole">The whole register</option>
              <option value="narrow">Part of it…</option>
            </select>
          </div>
          {!narrow && <button type="submit" className="btn btn-primary">Grant</button>}
        </div>
        {narrow && (
          <div className="as-grant-scope">
            <div className="as-inline-form" style={{ margin: 0 }}>
              {companies && (
                <div className="form-group">
                  <label htmlFor="as-g-company">Company</label>
                  <select id="as-g-company" value={form.company_id}
                    onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                    <option value="">Any</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="as-g-cat">Category</label>
                <select id="as-g-cat" value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Any</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="as-g-place">Place</label>
                <select id="as-g-place" value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <option value="">Anywhere</option>
                  {places.map((p) => <option key={p.id} value={p.id}>{p.path}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex: '0 1 10rem' }}>
                <label htmlFor="as-g-tag">Or one device, by tag</label>
                <input id="as-g-tag" value={form.asset_tag} placeholder="IT-LAP-000042"
                  onChange={(e) => setForm({ ...form, asset_tag: e.target.value.toUpperCase() })} />
              </div>
              <button type="submit" className="btn btn-primary">Grant</button>
            </div>
            {form.asset_tag.trim() && (form.company_id || form.category_id || form.location_id) && (
              <p className="as-quiet" style={{ margin: '0.5rem 0 0' }}>A grant on one device is about that device alone; the other choices will be ignored.</p>
            )}
          </div>
        )}
      </form>
      {form.user_id && (
        <p className="as-quiet" data-testid="grant-preview">
          {`${chosenName || 'They'} will be able to `
            + `${form.level === 'write' ? 'look at and change' : 'look at'} ${grantScope(preview)}`
            + `${form.who === 'contractor' ? ', from their portal' : ''}.`}
        </p>
      )}

      {byPerson.length === 0
        ? <p className="as-quiet">No grants yet. Administrators already have the whole register.</p>
        : byPerson.map(([uid, p]) => (
          <div key={uid} className="as-panel" style={{ marginBottom: '0.75rem' }}>
            <h2>{p.name}{p.contractor ? <span className="as-quiet"> · contractor</span> : null}</h2>
            <ul className="as-tree">
              {p.grants.map((g) => (
                <li key={g.id}>
                  <span className="as-chip" data-tone={g.level === 'write' ? 'warning' : 'info'}>
                    {g.level === 'write' ? 'Change' : 'Look'}
                  </span>
                  <span>{grantScope(g)}</span>
                  <span className="as-spacer" />
                  <button type="button" className="btn btn-plain btn-small" onClick={() => remove(g.id)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

/**
 * How far ahead to warn about warranties ending and replacement dates. The
 * bell, the list's "Ending soon" filter and the automation rules all read
 * this one number, so they are always about the same devices.
 */
function NoticeDays({ value, onSaved }) {
  const [days, setDays] = useState(String(value ?? 30));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.put(`${API_BASE}/app-settings/assets-warranty-notice-days`, { value: Number(days) });
      onSaved();
    } catch (e) {
      setError(errorOf(e, 'Could not save that.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="ar-rule" id="s-warranty">
      <span className="ar-label">Warn about warranties and replacements</span>
      <p className="ar-consequence">
        {`${value ?? 30} days ahead: the bell counts them, the list's "Ending soon" filter shows them, and any automation rule listening for them runs once for each.`}
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <div className="as-inline-form">
        <div className="form-group" style={{ flex: '0 1 8rem' }}>
          <label htmlFor="as-notice-days">Days ahead</label>
          <input id="as-notice-days" type="number" min={1} max={365} value={days}
            onChange={(e) => setDays(e.target.value)} />
        </div>
        <button type="button" className="btn btn-secondary" disabled={busy || String(value) === days}
          onClick={save}>Save</button>
      </div>
    </div>
  );
}

/**
 * A label size of IT's own, for stock that is none of the common ones the
 * print screen already offers. Measured off the sheet with a ruler: the
 * label's width and height, how many across and down, the margin to the
 * first label, and the gap between labels.
 */
const LABEL_FIELDS = [
  ['width', 'Label width'], ['height', 'Label height'], ['columns', 'Across'], ['rows', 'Down'],
  ['top', 'Top margin'], ['left', 'Left margin'], ['gap_x', 'Gap across'], ['gap_y', 'Gap down']
];

function LabelSize({ value, onSaved }) {
  const [form, setForm] = useState(() => value || {
    width: '', height: '', columns: '', rows: '', top: 0, left: 0, gap_x: 0, gap_y: 0
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (clear = false) => {
    setBusy(true);
    setError('');
    try {
      await axios.put(`${API_BASE}/app-settings/assets-label-custom`, {
        value: clear ? null : Object.fromEntries(LABEL_FIELDS.map(([k]) => [k, Number(form[k] || 0)]))
      });
      if (clear) setForm({ width: '', height: '', columns: '', rows: '', top: 0, left: 0, gap_x: 0, gap_y: 0 });
      onSaved();
    } catch (e) {
      setError(errorOf(e, 'Could not save the label size.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="ar-rule" id="s-label-size">
      <span className="ar-label">Your own label size</span>
      <p className="ar-consequence">
        {value
          ? `Offered on the print screen as ${value.width} × ${value.height} mm, ${value.columns} across and ${value.rows} down.`
          : 'Only needed if your label stock is none of the Avery, Brother or Dymo sizes the print screen offers. In millimetres.'}
      </p>
      {error && <p className="as-error" role="alert">{error}</p>}
      <div className="as-inline-form">
        {LABEL_FIELDS.map(([k, label]) => (
          <div className="form-group" key={k} style={{ flex: '0 1 7rem' }}>
            <label htmlFor={`as-lbl-${k}`}>{label}</label>
            <input id={`as-lbl-${k}`} type="number" min={0} step={k === 'columns' || k === 'rows' ? 1 : 0.1}
              value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </div>
        ))}
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => save(false)}>Save size</button>
        {value && <button type="button" className="btn btn-plain" disabled={busy} onClick={() => save(true)}>Remove</button>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- devices

/**
 * The device agents: whether Jarvis accepts their reports, how much they say
 * about location, the tokens their installers use, and the office networks
 * that make "in the Valletta office" possible without any geolocation.
 *
 * The switch that starts it all is the first thing on the screen, with what
 * has to have happened before it is turned on said beside it - because
 * turning it on is the moment the firm starts monitoring its devices.
 */
/**
 * The MAC maker registry (migration 100): which copy Discovery names makers
 * from - the one bundled with this release, or one the server fetched from
 * the IEEE - with "Refresh now" and the monthly refresh switch. A refresh
 * downloads about 4 MB and can take a minute; a download that is not the
 * registry leaves the copy in use as it was.
 */
function MakerRegistry({ auto, onSwitch }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const load = useCallback(() => axios.get(`${API_BASE}/assets/oui-registry`)
    .then((r) => setInfo(r.data))
    .catch((e) => setError(errorOf(e, 'Could not read the maker registry.'))), []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    setError('');
    setDone('');
    try {
      const r = await axios.post(`${API_BASE}/assets/oui-registry/refresh`, {});
      setInfo(r.data.status);
      setDone(`Refreshed: ${Number(r.data.blocks).toLocaleString('en-GB')} maker blocks.`);
    } catch (e) {
      setError(errorOf(e, 'Could not refresh the maker registry.'));
    } finally {
      setBusy(false);
    }
  };

  const when = (t) => new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const blocks = (n) => Number(n || 0).toLocaleString('en-GB');
  return (
    <div className="ar-rule" id="s-maker-registry" data-testid="maker-registry">
      <span className="ar-label">MAC maker registry</span>
      <p className="ar-consequence">
        Discovery names a device’s maker from the first half of its MAC address, using the IEEE’s official
        registry of address blocks. The IEEE adds blocks every month; a device from a newer block shows
        “MAC block not in the list” until the registry is refreshed.
      </p>
      {info?.missing && <p className="as-note" data-tone="warning">{info.missing}</p>}
      {info && !info.missing && (
        <p className="as-quiet" data-testid="maker-registry-status">
          {info.source === 'refreshed'
            ? `In use: refreshed from the IEEE on ${when(info.refreshed_at)}${info.refreshed_by ? ` by ${info.refreshed_by}` : ' by itself'} · ${blocks(info.blocks)} maker blocks.`
            : `In use: the copy bundled with this version of Jarvis · ${blocks(info.blocks)} maker blocks. Never refreshed here.`}
        </p>
      )}
      {error && <p className="as-error" role="alert">{error}</p>}
      {done && <p className="as-note" data-tone="success">{done}</p>}
      <p>
        <button type="button" className="btn btn-secondary btn-small" disabled={busy || !info || Boolean(info.missing)} onClick={refresh}>
          {busy ? 'Downloading from the IEEE…' : 'Refresh now'}
        </button>
      </p>
      <div className="ar-rule ar-rule-sub" id="s-maker-registry-auto">
        <label className="ar-check">
          <input type="checkbox" checked={auto} disabled={!info || Boolean(info.missing)} onChange={onSwitch} />
          <span className="ar-label">Keep it up to date by itself</span>
        </label>
        <p className="ar-consequence">
          {auto
            ? `On: once the copy in use is over ${info?.stale_days || 30} days old, the server downloads the registry again from the IEEE’s site. Only maker names come back; nothing about the firm is sent.`
            : 'Off: the registry changes only when somebody presses Refresh now, or with a new release of Jarvis.'}
        </p>
      </div>
    </div>
  );
}

function Devices({ half = 'agent' }) {
  const [rules, setRules] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [nets, setNets] = useState([]);
  const [places, setPlaces] = useState([]);
  const [fresh, setFresh] = useState(null);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('Intune deployment');
  const [days, setDays] = useState('30');
  const BLANK_NET = { name: '', kind: 'office', ssid: '', public_ip: '', subnet: '', gateway_mac: '', location_id: '' };
  const [net, setNet] = useState(BLANK_NET);
  const [editingNet, setEditingNet] = useState(null);
  const [here, setHere] = useState(null);
  // The routers the devices report (30 October), with the MAC block's maker.
  const [gateways, setGateways] = useState([]);
  const [offline, setOffline] = useState('');
  // Self-installs waiting for a decision (069). Null before that migration,
  // when the queue does not exist and the section is not shown.
  const [waiting, setWaiting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, t, n, l, q] = await Promise.all([
        axios.get(`${API_BASE}/app-settings`),
        axios.get(`${API_BASE}/asset-admin/enrolment-tokens`),
        axios.get(`${API_BASE}/asset-admin/office-networks`),
        axios.get(`${API_BASE}/asset-admin/locations`),
        axios.get(`${API_BASE}/asset-admin/enrolment-requests`).catch((e) => {
          if (e.response?.status === 503) return { data: null };
          throw e;
        })
      ]);
      setRules(r.data || {});
      setOffline(String(r.data?.assets_offline_days ?? 14));
      setTokens(t.data || []);
      setWaiting(Array.isArray(q.data) ? q.data : null);
      setNets(n.data || []);
      setPlaces(l.data || []);
      axios.get(`${API_BASE}/asset-admin/office-networks/gateways`)
        .then((g) => setGateways(Array.isArray(g.data) ? g.data : []), () => setGateways([]));
      setError('');
    } catch (e) {
      setError(errorOf(e, 'Could not load the device settings.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setError('');
    try { const r = await fn(); await load(); return r; } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      return null;
    }
  };

  if (error && !rules) return <p className="as-note" data-tone="warning">{error}</p>;
  if (!rules) return <p className="as-quiet">Loading…</p>;
  const on = rules.assets_heartbeat_enabled === true;
  const api = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

  return (
    <div className="as-section">
      {error && <p className="as-error" role="alert">{error}</p>}

      {half === 'agent' && (
      <>
      <div className="ar-rule" id="s-heartbeat">
        <label className="ar-check">
          <input type="checkbox" checked={on}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-heartbeat-enabled`, { value: !on }))} />
          <span className="ar-label">Accept reports from the device agent</span>
        </label>
        <p className="ar-consequence">
          {on
            ? 'On: enrolled laptops and desktops report their serial, OS, encryption and disks once a day.'
            : 'Off: an agent that is already installed waits and sends nothing.'}
        </p>
        {!on && (
          <p className="as-note" data-tone="warning">
            Before switching this on, staff must have been told what is collected and why, and the
            proportionality assessment must be done. This is workplace monitoring under GDPR.
          </p>
        )}
        <p className="ar-consequence" id="s-heartbeat-signed-in">
          The report also says who is signed in to the machine and who signed in over the last fourteen
          days (agent 0.5.0 and later), matched to people by their address. Nothing changes who holds a
          device: that is a button on the Asset Portal → Devices list, written into the device’s history.
        </p>
      </div>

      <div className="ar-rule" id="s-heartbeat-location">
        <span className="ar-label">Location</span>
        <p className="ar-consequence">
          Coarse means the office network a device is on - from the list below - or simply
          “away from the office”. There is no firm-wide precise setting: precise location is only
          ever for one device, while it is marked lost, from its own page.
        </p>
        <div className="as-subtabs">
          {['off', 'coarse'].map((v) => (
            <button key={v} type="button" className="stg-tab" data-on={String(rules.assets_heartbeat_location === v)}
              onClick={() => act(() => axios.put(`${API_BASE}/app-settings/assets-heartbeat-location`, { value: v }))}>
              {v === 'off' ? 'Off' : 'Coarse'}
            </button>
          ))}
        </div>
      </div>

      <div className="ar-rule" id="s-offline-days">
        <span className="ar-label">Not reporting after</span>
        <div className="as-inline-form">
          <div className="form-group" style={{ flex: '0 1 8rem' }}>
            <label htmlFor="as-offline-days">Days</label>
            <input id="as-offline-days" type="number" min={1} max={365} value={offline}
              onChange={(e) => setOffline(e.target.value)} />
          </div>
          <button type="button" className="btn btn-secondary" disabled={String(rules.assets_offline_days) === offline}
            onClick={() => act(() => axios.put(`${API_BASE}/app-settings/assets-offline-days`, { value: Number(offline) }))}>
            Save
          </button>
        </div>
      </div>

      <div className="ar-rule" id="s-agent-auto-update">
        <label className="ar-check">
          <input type="checkbox" checked={rules.agent_auto_update === true}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/agent-auto-update`,
              { value: rules.agent_auto_update !== true }))} />
          <span className="ar-label">Agents update themselves</span>
        </label>
        <p className="ar-consequence">
          {rules.agent_auto_update === true
            ? 'On: an agent older than the version on the Install page is told so at its next check-in, downloads that version from Jarvis, checks its hash and replaces itself.'
            : 'Off: agents stay at the version they were installed with; IT updates them from the Install page or with Intune.'}
        </p>
      </div>

      <div className="ar-rule" id="s-diagnostics">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_diagnostics_enabled === true}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-diagnostics-enabled`,
              { value: rules.assets_diagnostics_enabled !== true }))} />
          <span className="ar-label">Allow diagnostics on request</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_diagnostics_enabled === true
            ? 'On: IT can ask a device to run read-only checks from a fixed menu - uptime, updates, what is using the processor, disks, network, recent errors. The person at the machine sees a notice naming who asked.'
            : 'Off: nobody can ask a device for a diagnostic, and an agent hands none back.'}
        </p>
      </div>

      <div className="ar-rule" id="s-heartbeat-attached">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_heartbeat_attached === true}
            disabled={!on}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-heartbeat-attached`,
              { value: rules.assets_heartbeat_attached !== true }))} />
          <span className="ar-label">List what is plugged into each device</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_heartbeat_attached === true
            ? 'On: the daily report lists the screens, docks, printers, drives, keyboards, mice, webcams and headsets attached to the machine, with make, model and serial where the device says. A screen or drive the register does not have goes to the Discovery inbox; one it has is marked as seen with that machine.'
            : 'Off: the agent does not look at what is plugged in. Turning this on widens what is collected - a USB stick somebody plugs in is listed - so the staff notice must say so first.'}
        </p>
      </div>

      <div className="ar-rule" id="s-attached-autolink">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_attached_autolink !== false}
            disabled={!on || rules.assets_heartbeat_attached !== true}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-attached-autolink`,
              { value: rules.assets_attached_autolink === false }))} />
          <span className="ar-label">Link plugged-in devices to the computer at once</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_attached_autolink !== false
            ? 'On: a screen, dock, keyboard, mouse, webcam or headset a computer reports is added to the register straight away, plugged into that computer, with its holder and place. Whoever may change the device can unlink it from its page, and an unlinked device is not linked back by the next report. A network printer, a USB stick and anything over Bluetooth still wait in the Discovery inbox; a thing the holder has marked personal is never linked.'
            : 'Off: everything a computer reports plugged in waits in the Discovery inbox for IT to accept.'}
        </p>
      </div>

      <div className="ar-rule ar-rule-sub" id="s-attached-autolink-office-only">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_attached_autolink_office_only !== false}
            disabled={!on || rules.assets_heartbeat_attached !== true || rules.assets_attached_autolink === false}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-attached-autolink-office-only`,
              { value: rules.assets_attached_autolink_office_only === false }))} />
          <span className="ar-label">Only when the computer is in an office</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_attached_autolink_office_only !== false
            ? 'On: a thing is linked at once only when the computer’s report places it in one of the offices below. Seen at home or at a client, it waits in the Discovery inbox marked with where it was, for one Accept; after that it is recognised by its serial wherever it turns up. With location reporting off, nothing says “not the office”, so everything links.'
            : 'Off: a thing is linked wherever the computer is - a screen at somebody’s home becomes the firm’s device as soon as it is seen.'}
        </p>
      </div>

      {/* 108: no switch of its own - it rides on the plugged-in list above. */}
      <p className="ar-consequence" id="s-desk-sittings">
        Desks: a screen or dock that belongs to a desk is never linked to a computer. When a computer reports one,
        Jarvis records instead that its holder sat at that desk, from when to when - seen by IT administrators only,
        on the desk’s page, and kept 90 days.
      </p>

      <div className="ar-rule" id="s-network-scan">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_network_scan === true}
            disabled={!on || rules.assets_diagnostics_enabled !== true}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-network-scan`,
              { value: rules.assets_network_scan !== true }))} />
          <span className="ar-label">Allow a scan of the office network</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_network_scan === true
            ? 'On: from the Discovery page, IT can ask an office PC to try every address on its subnet. Printers, switches, access points, storage, cameras and desk phones that answer go to the inbox - and so do staff phones on the Wi-Fi, which IT ignores there. Only ever from a device whose last report placed it in an office below, never from a home or a client’s site. Read-only: the scan asks, it changes nothing.'
            : 'Off: nobody can ask for a scan. Needs diagnostics on. It sees every device on the office network, so the staff notice must say so first.'}
        </p>
      </div>

      <MakerRegistry auto={rules.assets_oui_auto_refresh === true}
        onSwitch={() => act(() => axios.put(`${API_BASE}/app-settings/assets-oui-auto-refresh`,
          { value: rules.assets_oui_auto_refresh !== true }))} />

      <div className="ar-rule" id="s-diagnostics-contractor">
        <label className="ar-check">
          <input type="checkbox" checked={rules.assets_diagnostics_contractor !== false}
            disabled={rules.assets_diagnostics_enabled !== true}
            onChange={() => act(() => axios.put(`${API_BASE}/app-settings/assets-diagnostics-contractor`,
              { value: rules.assets_diagnostics_contractor === false }))} />
          <span className="ar-label">Let the contractor on a ticket ask too</span>
        </label>
        <p className="ar-consequence">
          {rules.assets_diagnostics_contractor !== false
            ? 'On: a contractor fixing an open ticket about a device may run the reduced set - uptime, updates, processor and memory, disks, network. Never the list of installed programs, start-up items or the error log.'
            : 'Off: only IT may ask.'}
        </p>
      </div>
      </>
      )}

      {half === 'enrolment' && (
      <>
      {waiting && (
        <>
          <h3 id="s-waiting">Waiting for your approval</h3>
          <p className="as-quiet">
            A machine installed from somebody’s download, whose serial already has an agent reporting.
            It is held until you decide, so a download cannot take over a device. Approve it if it is
            the same computer (a reinstall, a new disk or board); the previous agent is then stopped.
          </p>
          {waiting.length === 0 && <p className="as-quiet" data-testid="enrol-waiting-none">Nothing is waiting.</p>}
          {waiting.length > 0 && (
            <ul className="as-tree" data-testid="enrol-waiting">
              {waiting.map((q) => (
                <li key={q.id}>
                  <strong>{q.asset_tag}</strong>
                  <span className="as-chip" data-tone="warning">Waiting</span>
                  <span className="as-quiet">
                    {[
                      `${q.hostname || 'a machine'} (${q.platform === 'macos' ? 'Mac' : 'Windows'})`,
                      `installed from ${q.requested_for_name || q.requested_for_email || 'a download'}`,
                      new Date(q.requested_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
                      q.current_last_seen_at
                        ? `the current agent (${q.current_hostname || 'this device'}) last reported ${new Date(q.current_last_seen_at).toLocaleDateString('en-GB')}`
                        : 'the current agent has not reported yet'
                    ].join(' · ')}
                  </span>
                  <span className="as-spacer" />
                  <button type="button" className="btn btn-primary btn-small"
                    onClick={() => act(() => axios.post(`${API_BASE}/asset-admin/enrolment-requests/${q.id}/approve`, {}))}>
                    Approve
                  </button>
                  <button type="button" className="btn btn-plain btn-small"
                    onClick={() => act(() => axios.post(`${API_BASE}/asset-admin/enrolment-requests/${q.id}/reject`, {}))}>
                    Reject
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <h3 id="s-tokens">Enrolment tokens</h3>
      <p className="as-quiet">
        For Intune and installs from the IT package: a token enrols machines, and each machine then has
        its own key. It is shown once. People installing Jarvis themselves do not need one - the
        installer they download carries a one-shot token made for them, listed below.
      </p>
      <div className="as-inline-form">
        <div className="form-group">
          <label htmlFor="as-tok-label">What it is for</label>
          <input id="as-tok-label" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: '0 1 7rem' }}>
          <label htmlFor="as-tok-days">Lasts (days)</label>
          <input id="as-tok-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
        </div>
        <button type="button" className="btn btn-primary"
          onClick={async () => {
            const r = await act(() => axios.post(`${API_BASE}/asset-admin/enrolment-tokens`, { label, days: Number(days) }));
            if (r) setFresh(r.data);
          }}>Make a token</button>
      </div>
      {fresh && (
        <div className="as-note" data-tone="warning">
          <p style={{ margin: '0 0 0.4rem' }}><strong>Copy this now - Jarvis cannot show it again.</strong></p>
          <p className="as-tag" style={{ overflowWrap: 'anywhere' }}>{fresh.token}</p>
          <p style={{ margin: '0.4rem 0 0' }}>Intune install command (Windows, run as System):</p>
          <p className="as-tag" style={{ overflowWrap: 'anywhere' }}>
            {`jarvis-agent.exe install --server ${api || 'https://<jarvis-api>'} --token ${fresh.token}`}
          </p>
        </div>
      )}
      {tokens.some((t) => t.purpose !== 'self-install') && (
        <ul className="as-tree">
          {tokens.filter((t) => t.purpose !== 'self-install').map((t) => (
            <li key={t.id}>
              <strong>{t.label}</strong>
              <span className="as-chip" data-tone={t.live ? 'success' : 'idle'}>
                {t.revoked_at ? 'Revoked' : t.live ? 'Live' : 'Expired'}
              </span>
              <span className="as-quiet">
                {`${t.uses} machine${t.uses === 1 ? '' : 's'} enrolled · until ${new Date(t.expires_at).toLocaleDateString('en-GB')}`}
              </span>
              <span className="as-spacer" />
              {t.live && (
                <button type="button" className="btn btn-plain btn-small"
                  onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/enrolment-tokens/${t.id}`))}>
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {tokens.some((t) => t.purpose === 'self-install') && (
        <>
          <p className="ar-label" style={{ margin: '1.1rem 0 0.4rem' }}>Installer downloads, last 30 days</p>
          <ul className="as-tree" data-testid="installer-downloads">
            {tokens.filter((t) => t.purpose === 'self-install').map((t) => (
              <li key={t.id}>
                <strong>{t.created_by_name || 'Somebody'}</strong>
                <span className="as-chip" data-tone={t.uses > 0 ? 'success' : 'idle'}>
                  {t.revoked_at ? 'Revoked' : t.uses > 0 ? 'Used' : t.live ? 'Not used yet' : 'Expired'}
                </span>
                <span className="as-quiet">
                  {`${t.issued_platform === 'windows-x64' ? 'Windows' : t.issued_platform ? 'Mac' : ''} · downloaded ${new Date(t.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`}
                </span>
                <span className="as-spacer" />
                {t.live && (
                  <button type="button" className="btn btn-plain btn-small"
                    onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/enrolment-tokens/${t.id}`))}>
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 id="s-devices">Devices reporting</h3>
      <p className="as-quiet">
        Every machine with an agent, who has it and who is signed in, is listed in the{' '}
        <Link to="/assets/devices">Asset Portal → Devices</Link>.
      </p>

      <h3 id="s-networks">Known networks</h3>
      <p className="as-quiet">
        A device on one of these is shown as at that office or client site, and a scan may run only from
        an office. Any one of the four ways of recognising it is enough; the public address is the surest.
        {' '}“Use this network” fills the form from where you are now.
      </p>
      {here && (
        <p className="as-note" data-tone="info">
          {`Your browser came from ${here.public_ip || 'an address Jarvis could not read'}.`}
          {here.from_device
            ? ` Your ${here.from_device.asset_tag} reported ${here.ssid ? `Wi-Fi “${here.ssid}”` : 'no Wi-Fi name'}${here.subnet ? `, subnet ${here.subnet}` : ''}${here.gateway_mac ? `, gateway ${here.gateway_mac}${here.gateway_maker ? ` (${here.gateway_maker})` : ''}` : ''}${here.from_device.same_connection ? '' : ' - from a different connection, so only the address was used'}.`
            : ' None of your own devices has reported in the last day, so only the address is known.'}
          {here.already ? ` This address is already ${here.already.name}.` : ''}
        </p>
      )}
      <div className="as-inline-form" data-testid="network-form">
        <div className="form-group">
          <label htmlFor="as-net-name">Name</label>
          <input id="as-net-name" value={net.name} onChange={(e) => setNet({ ...net, name: e.target.value })} placeholder="Valletta office" />
        </div>
        <div className="form-group" style={{ flex: '0 1 8rem' }}>
          <label htmlFor="as-net-kind">Kind</label>
          <select id="as-net-kind" value={net.kind} onChange={(e) => setNet({ ...net, kind: e.target.value })}>
            <option value="office">Office</option>
            <option value="client">Client site</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="as-net-ssid">Wi-Fi name</label>
          <input id="as-net-ssid" value={net.ssid} onChange={(e) => setNet({ ...net, ssid: e.target.value })} />
        </div>
        <div className="form-group">
          <label htmlFor="as-net-ip">Public address</label>
          <input id="as-net-ip" value={net.public_ip} onChange={(e) => setNet({ ...net, public_ip: e.target.value })} />
        </div>
        <div className="form-group">
          <label htmlFor="as-net-subnet">Subnet</label>
          <input id="as-net-subnet" value={net.subnet} placeholder="10.0.0.0/24" onChange={(e) => setNet({ ...net, subnet: e.target.value })} />
        </div>
        <div className="form-group">
          <label htmlFor="as-net-gw">Gateway MAC</label>
          <input id="as-net-gw" value={net.gateway_mac} placeholder="aa:bb:cc:dd:ee:ff" onChange={(e) => setNet({ ...net, gateway_mac: e.target.value })} />
        </div>
        <div className="form-group">
          <label htmlFor="as-net-place">Place</label>
          <select id="as-net-place" value={net.location_id} onChange={(e) => setNet({ ...net, location_id: e.target.value })}>
            <option value="">None</option>
            {places.map((p) => <option key={p.id} value={p.id}>{p.path}</option>)}
          </select>
        </div>
        <button type="button" className="btn btn-plain"
          onClick={async () => {
            try {
              const r = await axios.get(`${API_BASE}/asset-admin/office-networks/here`);
              setHere(r.data);
              const d = r.data || {};
              setNet((n) => ({ ...n, public_ip: d.public_ip || n.public_ip, ssid: d.ssid || n.ssid,
                subnet: d.subnet || n.subnet, gateway_mac: d.gateway_mac || n.gateway_mac }));
            } catch (e) { setError(errorOf(e, 'Could not look at this network.')); }
          }}>Use this network</button>
        <button type="button" className="btn btn-secondary"
          onClick={async () => {
            const body = { ...net, location_id: net.location_id || null,
              ssid: net.ssid || null, public_ip: net.public_ip || null, subnet: net.subnet || null, gateway_mac: net.gateway_mac || null };
            const r = await act(() => (editingNet
              ? axios.put(`${API_BASE}/asset-admin/office-networks/${editingNet}`, body)
              : axios.post(`${API_BASE}/asset-admin/office-networks`, body)));
            if (r) { setNet(BLANK_NET); setEditingNet(null); setHere(null); }
          }}>{editingNet ? 'Save' : 'Add'}</button>
        {editingNet && (
          <button type="button" className="btn btn-plain" onClick={() => { setNet(BLANK_NET); setEditingNet(null); }}>Cancel</button>
        )}
      </div>
      {nets.length > 0 && (
        <ul className="as-tree">
          {nets.map((n) => (
            <li key={n.id}>
              <strong>{n.name}</strong>
              {n.kind === 'client' && <span className="as-chip" data-tone="info">Client site</span>}
              <span className="as-quiet">{[n.ssid && `Wi-Fi ${n.ssid}`, n.public_ip, n.subnet, n.gateway_mac && `gateway ${n.gateway_mac}`, n.location_path].filter(Boolean).join(' · ')}</span>
              <span className="as-spacer" />
              <button type="button" className="btn btn-plain btn-small"
                onClick={() => { setEditingNet(n.id); setNet({ name: n.name || '', kind: n.kind || 'office', ssid: n.ssid || '',
                  public_ip: n.public_ip || '', subnet: n.subnet || '', gateway_mac: n.gateway_mac || '', location_id: n.location_id || '' }); }}>Edit</button>
              <button type="button" className="btn btn-plain btn-small"
                onClick={() => act(() => axios.delete(`${API_BASE}/asset-admin/office-networks/${n.id}`))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      {gateways.length > 0 && (
        <>
          <h4 id="s-gateways">Routers your devices see</h4>
          <p className="as-quiet">
            The gateway each device reported in the last 7 days, and who made it (from its MAC). “Use” puts it in the form above.
          </p>
          <ul className="as-tree" data-testid="gateway-list">
            {gateways.map((g) => (
              <li key={g.mac}>
                <strong>{g.maker || 'Maker not known'}</strong>
                {g.network && <span className="as-chip" data-tone="info">{g.network}</span>}
                <span className="as-quiet">
                  {[g.mac, g.gateway, g.subnet, g.ssids?.length ? `Wi-Fi ${g.ssids.join(', ')}` : null,
                    `${g.devices} device${g.devices === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                </span>
                <span className="as-spacer" />
                {!g.network && (
                  <button type="button" className="btn btn-plain btn-small"
                    onClick={() => setNet((n) => ({ ...n, gateway_mac: g.mac, subnet: n.subnet || g.subnet || '',
                      ssid: n.ssid || (g.ssids?.length === 1 ? g.ssids[0] : '') }))}>Use</button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      </>
      )}
    </div>
  );
}
